import React, { useState } from 'react';
import { useToolPresets, ToolStep } from '@/hooks/useToolPresets';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Play, Trash2, Save, Workflow } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

const AVAILABLE_TOOLS = [
  { id: 'resize', name: 'Resize & Crop' },
  { id: 'compress', name: 'Compress' },
  { id: 'filters', name: 'Filters' },
  { id: 'background-remove', name: 'Remove Background' },
  { id: 'watermark', name: 'Watermark' },
  { id: 'format-convert', name: 'Convert Format' },
];

export const PresetBuilder: React.FC = () => {
  const { presets, loading, createPreset, deletePreset } = useToolPresets();
  const { toast } = useToast();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [newPreset, setNewPreset] = useState({
    name: '',
    description: '',
    category: '',
  });
  const [toolsChain, setToolsChain] = useState<ToolStep[]>([]);

  const handleAddTool = () => {
    setToolsChain(prev => [...prev, { tool: '', settings: {} }]);
  };

  const handleRemoveTool = (index: number) => {
    setToolsChain(prev => prev.filter((_, i) => i !== index));
  };

  const handleToolChange = (index: number, toolId: string) => {
    setToolsChain(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], tool: toolId };
      return updated;
    });
  };

  const handleCreatePreset = async () => {
    if (!newPreset.name.trim()) {
      toast({
        title: 'Error',
        description: 'Preset name is required',
        variant: 'destructive',
      });
      return;
    }

    if (toolsChain.length === 0 || toolsChain.some(t => !t.tool)) {
      toast({
        title: 'Error',
        description: 'Please add at least one complete tool to the workflow',
        variant: 'destructive',
      });
      return;
    }

    await createPreset(
      newPreset.name,
      toolsChain,
      newPreset.description,
      newPreset.category
    );

    setNewPreset({ name: '', description: '', category: '' });
    setToolsChain([]);
    setIsCreateOpen(false);
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-muted-foreground">Loading presets...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Workflow Presets</h2>
          <p className="text-muted-foreground">Save and reuse tool workflows</p>
        </div>
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              New Preset
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create Workflow Preset</DialogTitle>
              <DialogDescription>
                Build a reusable workflow by chaining tools together
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Preset Name</Label>
                <Input
                  id="name"
                  value={newPreset.name}
                  onChange={(e) => setNewPreset(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="E.g., Social Media Pack"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description (optional)</Label>
                <Textarea
                  id="description"
                  value={newPreset.description}
                  onChange={(e) => setNewPreset(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="What does this preset do?"
                  rows={2}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="category">Category (optional)</Label>
                <Input
                  id="category"
                  value={newPreset.category}
                  onChange={(e) => setNewPreset(prev => ({ ...prev, category: e.target.value }))}
                  placeholder="E.g., Social Media, E-commerce"
                />
              </div>

              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <Label>Workflow Steps</Label>
                  <Button variant="outline" size="sm" onClick={handleAddTool}>
                    <Plus className="h-3 w-3 mr-1" />
                    Add Tool
                  </Button>
                </div>
                
                {toolsChain.length === 0 ? (
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <Workflow className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                      <p className="text-sm text-muted-foreground">
                        No tools added yet. Click "Add Tool" to start building your workflow.
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-2">
                    {toolsChain.map((step, index) => (
                      <div key={index} className="flex gap-2 items-center">
                        <Badge variant="outline" className="w-8 h-8 flex items-center justify-center">
                          {index + 1}
                        </Badge>
                        <Select
                          value={step.tool}
                          onValueChange={(value) => handleToolChange(index, value)}
                        >
                          <SelectTrigger className="flex-1">
                            <SelectValue placeholder="Select tool" />
                          </SelectTrigger>
                          <SelectContent>
                            {AVAILABLE_TOOLS.map((tool) => (
                              <SelectItem key={tool.id} value={tool.id}>
                                {tool.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveTool(index)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsCreateOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreatePreset}>
                <Save className="mr-2 h-4 w-4" />
                Save Preset
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {presets.length === 0 ? (
        <Card>
          <CardContent className="pt-6 text-center">
            <Workflow className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No presets yet</p>
            <p className="text-sm text-muted-foreground mt-1">
              Create a preset to automate your image processing workflow
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {presets.map((preset) => (
            <Card key={preset.id}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <CardTitle className="flex items-center gap-2">
                      <Workflow className="h-4 w-4" />
                      {preset.name}
                    </CardTitle>
                    {preset.description && (
                      <CardDescription className="mt-1">
                        {preset.description}
                      </CardDescription>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" title="Run workflow">
                      <Play className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => deletePreset(preset.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <p className="text-sm font-medium">Workflow Steps:</p>
                  <div className="flex flex-wrap gap-2">
                    {preset.tools_chain.map((step, i) => (
                      <Badge key={i} variant="secondary">
                        {i + 1}. {AVAILABLE_TOOLS.find(t => t.id === step.tool)?.name || step.tool}
                      </Badge>
                    ))}
                  </div>
                  {preset.category && (
                    <Badge className="mt-2">{preset.category}</Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};